import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Typography,
  Alert,
  Card,
  CardContent,
  Box,
  Divider,
  TextField,
  InputAdornment,
  Skeleton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Search from "@mui/icons-material/Search";
import { EventAvailable, CalendarMonth, Groups } from "@mui/icons-material";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import HubMedAdminFilters from "@/components/admin/hubmed/HubMedAdminFilters";
import HubMedOverviewStatCard from "@/components/admin/hubmed/HubMedOverviewStatCard";
import HubMedConnectedChemistsTable from "@/components/admin/hubmed/HubMedConnectedChemistsTable";

const MEDMATE_DARK  = "#004a99";
const MEDMATE_LIGHT = "#99c4ef";
const MEDMATE_BORDER = "#0062cc";

const CHEMIST_FILTER_DEBOUNCE_MS = 300;

function defaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return {
    dateStart: start.toISOString().split("T")[0],
    dateEnd:   end.toISOString().split("T")[0],
  };
}

function computeAvgPerWeek(total, dateStart, dateEnd) {
  if (!total || !dateStart || !dateEnd) return "0.00";
  const days = (new Date(dateEnd) - new Date(dateStart)) / (1000 * 60 * 60 * 24);
  const weeks = days / 7;
  if (!weeks) return "0.00";
  return (total / weeks).toFixed(2);
}

export default function MedmateAdminOverviewPage() {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const initial = useMemo(() => defaultDateRange(), []);
  const [dateStart, setDateStart] = useState(initial.dateStart);
  const [dateEnd,   setDateEnd]   = useState(initial.dateEnd);
  const [nameFilter, setNameFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const onDateRangeChange = useCallback((start, end) => {
    setDateStart(start || "");
    setDateEnd(end || "");
  }, []);

  // Debounce chemist name filter
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilter(nameFilter.trim()), CHEMIST_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [nameFilter]);

  // Fetch overview
  useEffect(() => {
    if (!dateStart || !dateEnd) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await phappsRequest(
          API_PATHS.MEDMATE_APPOINTMENT_OVERVIEW,
          "GET",
          null,
          { dateStart, dateEnd }
        );
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load Medmate overview.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dateStart, dateEnd]);

  // Derived values
  const totalAppointments = Number(data?.totalAppointments ?? 0);
  const avgWeek = computeAvgPerWeek(totalAppointments, dateStart, dateEnd);

  // Map byPharmacy → format expected by HubMedConnectedChemistsTable
  const chemistRows = useMemo(() => {
    const list = Array.isArray(data?.byPharmacy) ? data.byPharmacy : [];
    const filtered = debouncedFilter
      ? list.filter((r) =>
          (r.ChemistName ?? "").toLowerCase().includes(debouncedFilter.toLowerCase())
        )
      : list;
    return filtered.map((r) => ({
      ChemistID:             r.ChemistID,
      ChemistName:           r.ChemistName,
      totalAppointments:     r.appointmentCount,
      latestAppointmentDate: r.latestAppointmentDate ?? null,
    }));
  }, [data, debouncedFilter]);

  const activeChemistsCount = Array.isArray(data?.byPharmacy) ? data.byPharmacy.length : 0;
  const statsLoading = loading && !data;

  return (
    <div className="h-full min-h-0 flex flex-col bg-white overflow-x-hidden">
      <div className="flex-1 min-h-0 flex flex-col p-4 gap-3 w-full max-w-full">
        {loading && !data ? (
          <div className="flex flex-1 items-center justify-center min-h-[280px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: MEDMATE_BORDER }} />
              <p className="text-gray-600">Loading Medmate overview…</p>
            </div>
          </div>
        ) : (
          <>
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: MEDMATE_DARK, flexShrink: 0 }}>
                Medmate Admin overview
              </Typography>

              {error && (
                <Alert severity="warning" sx={{ flexShrink: 0 }}>
                  {error}
                </Alert>
              )}

              {/* Top row: date filter + stat cards */}
              <Box
                component="section"
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-stretch"
                sx={{ flexShrink: 0 }}
              >
                {/* Date filter — same Syncfusion picker as HubMed */}
                <div className="min-w-0 h-full flex">
                  <HubMedAdminFilters
                    dateStart={dateStart}
                    dateEnd={dateEnd}
                    onDateRangeChange={onDateRangeChange}
                  />
                </div>

                {/* Stat cards */}
                {statsLoading ? (
                  <>
                    {[1, 2, 3].map((k) => (
                      <div key={k} className="min-w-0 h-full flex">
                        <Skeleton variant="rounded" sx={{ borderRadius: 2, width: "100%", minHeight: 112, flex: 1 }} />
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <Box className="min-w-0 h-full flex">
                      <HubMedOverviewStatCard
                        title="Total appointments"
                        value={totalAppointments}
                        valueFormatter={(v) => Number(v || 0).toLocaleString()}
                        Icon={EventAvailable}
                        bgColor={MEDMATE_DARK}
                        borderColor={MEDMATE_LIGHT}
                        iconColor={MEDMATE_LIGHT}
                      />
                    </Box>
                    <Box className="min-w-0 h-full flex">
                      <HubMedOverviewStatCard
                        title="Appointments per week"
                        value={avgWeek}
                        valueFormatter={(v) => v}
                        Icon={CalendarMonth}
                        bgColor={MEDMATE_DARK}
                        borderColor={MEDMATE_LIGHT}
                        iconColor={MEDMATE_LIGHT}
                      />
                    </Box>
                    <Box className="min-w-0 h-full flex">
                      <HubMedOverviewStatCard
                        title="Active pharmacies"
                        value={activeChemistsCount}
                        valueFormatter={(v) => Number(v || 0).toLocaleString()}
                        Icon={Groups}
                        bgColor={MEDMATE_DARK}
                        borderColor={MEDMATE_LIGHT}
                        iconColor={MEDMATE_LIGHT}
                      />
                    </Box>
                  </>
                )}
              </Box>

              {/* Pharmacies table — full width (no consult-type pie for Medmate) */}
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    minHeight: { xs: 400, lg: 0 },
                    borderColor: MEDMATE_BORDER,
                    borderRadius: 2,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent sx={{ p: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        flexShrink: 0,
                      }}
                    >
                      <Typography fontWeight={700} sx={{ color: MEDMATE_DARK }}>
                        Pharmacies
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="Filter by pharmacy name"
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 260 }}
                      />
                    </Box>
                    <Divider />
                    <HubMedConnectedChemistsTable
                      embedded
                      fillHeight={isLgUp}
                      chemists={chemistRows}
                      loading={loading}
                      visible
                      useDocLinkGridStyle
                      showConsultBreakdown={false}
                      cardTitle="Pharmacies"
                    />
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </>
        )}
      </div>
    </div>
  );
}
